// apps/office/src/pages/office/Properties.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Plus, Search, Edit, Trash2, Eye,
  Home, MapPin, DollarSign, Bed, Bath,
  RefreshCw, Loader2, Filter, Grid3x3, List,
  Clock, Building2, Sparkles, ArrowUpDown,
  Copy, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const Properties = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('created');
  const [sortOrder, setSortOrder] = useState('desc');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);

  // Fetch properties from PocketBase
  const { data: properties = [], isLoading, refetch } = useQuery({
    queryKey: ['officeProperties', typeFilter, statusFilter],
    queryFn: async () => {
      try {
        let filter = 'type != "inquiry"';
        if (typeFilter !== 'all') {
          filter += ` && type = "${typeFilter}"`;
        }
        if (statusFilter !== 'all') {
          filter += ` && status = "${statusFilter}"`;
        }
        
        const records = await pb.collection('properties').getFullList({
          sort: '-created',
          filter: filter,
        });
        return records;
      } catch (error) {
        console.error('Error fetching properties:', error);
        toast.error('Failed to load properties');
        return [];
      }
    },
    enabled: !!user,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => pb.collection('properties').delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['officeProperties']);
      toast.success('Property deleted successfully');
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
    },
    onError: (error) => {
      console.error('Delete error:', error);
      toast.error('Failed to delete property: ' + (error.message || 'Unknown error'));
    },
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: async (property) => {
      const { id, created, updated, ...data } = property;
      data.title = `${data.title} (Copy)`;
      data.slug = `${data.slug}-copy-${Date.now()}`;
      data.status = 'draft';
      return await pb.collection('properties').create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['officeProperties']);
      toast.success('Property duplicated successfully');
    },
    onError: (error) => {
      console.error('Duplicate error:', error);
      toast.error('Failed to duplicate property');
    },
  });

  // Format price
  const formatPrice = (price) => {
    if (!price) return '?0';
    if (price >= 10000000) return `?${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `?${(price / 100000).toFixed(1)} L`;
    return `?${Number(price).toLocaleString('en-IN')}`;
  };

  // Filter and sort properties
  const filteredProperties = properties
    .filter((p) => {
      const search = searchTerm.toLowerCase();
      return (
        p.title?.toLowerCase().includes(search) ||
        p.city?.toLowerCase().includes(search) ||
        p.location?.toLowerCase().includes(search) ||
        p.slug?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      const direction = sortOrder === 'desc' ? -1 : 1;
      if (sortBy === 'price') {
        return (a.price - b.price) * direction;
      }
      if (sortBy === 'title') {
        return a.title?.localeCompare(b.title || '') * direction;
      }
      if (sortBy === 'created') {
        return (new Date(a.created) - new Date(b.created)) * direction;
      }
      return 0;
    });

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      active: { variant: 'default', icon: CheckCircle, label: 'Active', color: 'bg-green-100 text-green-700 border-green-200' },
      draft: { variant: 'secondary', icon: AlertCircle, label: 'Draft', color: 'bg-slate-100 text-slate-700 border-slate-200' },
      pending: { variant: 'outline', icon: Clock, label: 'Pending', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    };
    const config = statusMap[status] || statusMap.draft;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Get possession status badge
  const getPossessionBadge = (status) => {
    const statusMap = {
      ready_to_move: { label: 'Ready to Move', color: 'bg-green-100 text-green-700' },
      completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700' },
      under_construction: { label: 'Under Construction', color: 'bg-yellow-100 text-yellow-700' },
      upcoming: { label: 'Upcoming', color: 'bg-purple-100 text-purple-700' },
    };
    const config = statusMap[status] || statusMap.under_construction;
    return (
      <Badge className={`${config.color} border-0 text-xs`}>
        {config.label}
      </Badge>
    );
  };

  const handleDeleteClick = (property) => {
    setPropertyToDelete(property);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (propertyToDelete) {
      deleteMutation.mutate(propertyToDelete.id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Home className="w-6 h-6 text-indigo-600" />
            Properties
          </h1>
          <p className="text-slate-500 text-sm">Manage all property listings</p>
        </div>
        <Button 
          onClick={() => navigate('/office/properties/new')} 
          className="gap-2 bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4" />
          Add Property
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-800">{properties.length}</div>
            <p className="text-sm text-slate-500">Total Properties</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">
              {properties.filter(p => p.status === 'active').length}
            </div>
            <p className="text-sm text-slate-500">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-yellow-600">
              {properties.filter(p => p.status === 'pending').length}
            </div>
            <p className="text-sm text-slate-500">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-600">
              {properties.filter(p => p.status === 'draft').length}
            </div>
            <p className="text-sm text-slate-500">Drafts</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by title, city, location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Property Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="flat">Flats / Apartments</SelectItem>
                <SelectItem value="plot">Plots / Land</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created">Date Added</SelectItem>
                <SelectItem value="title">Title</SelectItem>
                <SelectItem value="price">Price</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                className="h-10 w-10 p-0"
              >
                <ArrowUpDown className={`w-4 h-4 transition-transform ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="h-10 w-10 p-0"
              >
                <Grid3x3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-10 w-10 p-0"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="flex justify-between items-center text-sm text-slate-500">
        <span>Showing {filteredProperties.length} properties</span>
        <Button variant="ghost" size="sm" onClick={() => refetch()} className="gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </Button>
      </div>

      {/* Properties Grid/List */}
      {filteredProperties.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16">
            <Home className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">No properties found</p>
            <p className="text-sm text-slate-400">
              {searchTerm ? 'Try adjusting your search filters' : 'Add your first property listing'}
            </p>
            {searchTerm && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setSearchTerm('')}
              >
                Clear Search
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProperties.map((property) => {
            const imageUrl = property.featured_image
              ? pb.files.getURL(property, property.featured_image)
              : null;
            
            return (
              <Card key={property.id} className="hover:shadow-lg transition-shadow overflow-hidden group">
                <CardContent className="p-0">
                  <div className="relative">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={property.title}
                        className="w-full h-52 object-cover group-hover:scale-105 transition duration-300"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-52 bg-slate-100 flex items-center justify-center">
                        <Home className="w-16 h-16 text-slate-300" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2 flex flex-col gap-1">
                      {getStatusBadge(property.status)}
                    </div>
                    <div className="absolute top-2 left-2 flex flex-col gap-1">
                      <Badge className="bg-white/90 backdrop-blur text-slate-700 border-0">
                        <Building2 className="w-3 h-3 mr-1" />
                        {property.type === 'flat' ? 'Apartment' : 'Plot'}
                      </Badge>
                      {getPossessionBadge(property.possession_status)}
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <h3 className="font-semibold text-slate-800 truncate">{property.title}</h3>
                        <div className="flex items-center gap-1 text-sm text-slate-500 mt-0.5">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{property.city}, {property.location}</span>
                        </div>
                      </div>
                      <span className="text-lg font-bold text-indigo-600 whitespace-nowrap">
                        {formatPrice(property.price)}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 mt-2 text-sm">
                      {property.type === 'flat' && (
                        <>
                          <span className="flex items-center gap-1 text-slate-600">
                            <Bed className="w-3 h-3" /> {property.bedrooms || 0} BHK
                          </span>
                          <span className="flex items-center gap-1 text-slate-600">
                            <Bath className="w-3 h-3" /> {property.bathrooms || 0}
                          </span>
                          {property.carpet_area && (
                            <span className="text-slate-500 text-xs">{property.carpet_area} sq.ft.</span>
                          )}
                        </>
                      )}
                      {property.type === 'plot' && property.plot_area && (
                        <span className="text-slate-500 text-xs">{property.plot_area} sq.ft.</span>
                      )}
                    </div>

                    <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-100">
                      <div className="text-xs text-slate-400">
                        {new Date(property.created).toLocaleDateString('en-IN')}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => navigate(`/property/${property.slug}`)}
                          target="_blank"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => navigate(`/office/properties/${property.id}/edit`)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => duplicateMutation.mutate(property)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteClick(property)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Property</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Possession</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredProperties.map((property) => (
                  <tr key={property.id} className="hover:bg-slate-50 transition">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                          {property.featured_image ? (
                            <img 
                              src={pb.files.getURL(property, property.featured_image)} 
                              alt={property.title}
                              className="w-full h-full object-cover"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Home className="w-4 h-4 text-slate-300" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 text-sm truncate max-w-[200px]">
                            {property.title}
                          </p>
                          <p className="text-xs text-slate-400 truncate max-w-[200px]">
                            {property.slug}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="text-xs">
                        {property.type === 'flat' ? 'Apartment' : 'Plot'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <MapPin className="w-3 h-3" /> {property.city}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-indigo-600">
                      {formatPrice(property.price)}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(property.status)}
                    </td>
                    <td className="px-4 py-3">
                      {getPossessionBadge(property.possession_status)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => navigate(`/property/${property.slug}`)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => navigate(`/office/properties/${property.id}/edit`)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => duplicateMutation.mutate(property)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteClick(property)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Property
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{propertyToDelete?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Properties;